import type { OrderItem } from '@/types'
import type { LineSelection } from '@/lib/lineModifiers'

/**
 * The two pure rules behind modifier support on the floor (F-MOD-N7).
 *
 * Extracted from KOTServerInterface so they can be tested without mounting the
 * whole ordering screen — both are rules where a regression is silent: the
 * ticket still looks plausible, it is just wrong about what the guest asked for.
 */

/**
 * Identity of a line's chosen options.
 *
 * Two lines fold into one only if this matches. Folding on product id alone
 * would merge a Large into a Regular — different food, different price — and
 * quietly discard one of the two choices the guest actually made.
 *
 * Sorted so selection ORDER never affects identity: picking Beef then Large
 * must be the same line as Large then Beef.
 */
export function selectionSignature(selections: ReadonlyArray<LineSelection>): string {
  return selections
    .map((s) => `${s.option_id}:${s.quantity}`)
    .sort()
    .join('|')
}

/**
 * Rebuild editable selections from a line the backend returned.
 *
 * Rows whose option has since been deleted keep their snapshotted name on the
 * order (F-MOD-N6) but have no option_id to re-select, so they are dropped
 * here — the line still shows what was sold, it just cannot be re-configured
 * against an option that no longer exists.
 */
export function selectionsFromOrderItem(oi: Pick<OrderItem, 'modifiers'>): LineSelection[] {
  return (oi.modifiers ?? [])
    .filter((m) => !!m.option_id)
    .map((m) => ({
      group_id: m.group_id ?? '',
      option_id: m.option_id as string,
      quantity: m.quantity,
    }))
}
